import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { Subject } from 'rxjs';
import { TtoCampaignVideoDto } from '@/app/common/general-types/tiktok-one-reporting.dto';
import {
  SparkCreatorVideoDto,
  SparkCreatorVideosDto,
} from '@/app/common/general-types/spark-ads-v3.dto';
import { TtoVideoAnalysisComponent } from '../../../reporting-tto/atoms/video-analysis/video-analysis';
import { SparkAdsReportingService } from '../../services/spark-ads-reporting.service';
import { SparkAdsStore } from '../../services/spark-ads-store';
import {
  SPARK_LIBRARY_FILTERS,
  SparkLibraryFilter,
  SparkLoadState,
} from '../../spark-ads.types';
import { VideoCard } from './atoms/video-card/video-card';
import { byEngagementRate, matchesFilter } from './creator-videos.helpers';

/**
 * Every video the brief's creators have posted to TikTok.
 *
 * Opening a card hands the post to the existing full-screen analysis modal, whose
 * Boosting tab carries the Spark authorisation and budget flow.
 */
@Component({
  selector: 'sa-creator-videos',
  imports: [VideoCard, TtoVideoAnalysisComponent],
  templateUrl: './creator-videos.html',
  styleUrl: './creator-videos.scss',
  host: { class: 'block w-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorVideos {
  readonly briefId = input.required<string>();

  private readonly reportingService = inject(SparkAdsReportingService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(SparkAdsStore);

  protected readonly loadState = signal<SparkLoadState>('idle');
  protected readonly loadError = signal<string | null>(null);
  protected readonly library = signal<SparkCreatorVideosDto | undefined>(undefined);

  /** The post open in the analysis modal, or undefined when the grid is showing. */
  protected readonly openVideo = signal<TtoCampaignVideoDto | undefined>(undefined);

  protected readonly filters = SPARK_LIBRARY_FILTERS;

  private readonly reload$ = new Subject<string>();

  /** Sorted strongest-performing first, then filtered by the selected pill. */
  protected readonly visibleVideos = computed<SparkCreatorVideoDto[]>(() => {
    const videos = this.library()?.videos ?? [];
    const filter = this.store.libraryFilter();
    return [...videos].sort(byEngagementRate).filter((video) => matchesFilter(video, filter));
  });

  protected readonly totalCount = computed(() => this.library()?.videos.length ?? 0);

  protected readonly isEmptyLibrary = computed(
    () => this.loadState() === 'loaded' && this.totalCount() === 0,
  );

  protected readonly hasNoMatches = computed(
    () => this.loadState() === 'loaded' && this.totalCount() > 0 && this.visibleVideos().length === 0,
  );

  constructor() {
    this.reload$
      .pipe(
        switchMap((briefId) => {
          this.loadState.set('loading');
          this.loadError.set(null);
          return this.reportingService.getCreatorVideos(briefId).pipe(
            catchError((error: unknown) => {
              this.loadError.set(this.messageFor(error));
              this.loadState.set('error');
              return of(undefined);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((library) => {
        if (library) {
          this.library.set(library);
          this.loadState.set('loaded');
        }
      });

    // `briefId` is a required input, so wait for it to arrive before fetching.
    queueMicrotask(() => this.reload$.next(this.briefId()));
  }

  protected reload(): void {
    this.reload$.next(this.briefId());
  }

  protected selectFilter(filter: SparkLibraryFilter): void {
    this.store.setLibraryFilter(filter);
  }

  /** Count shown beside each filter pill. */
  protected countFor(filter: SparkLibraryFilter): number {
    const counts = this.library()?.counts;
    if (!counts) {
      return 0;
    }
    return filter === 'all' ? this.totalCount() : counts[filter];
  }

  protected getFilterClass(filter: SparkLibraryFilter): string {
    return this.store.libraryFilter() === filter
      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  protected onOpenVideo(entry: SparkCreatorVideoDto): void {
    this.openVideo.set(entry.video);
  }

  /**
   * Closing the modal refreshes the library: the user may have requested or applied a
   * Spark authorisation, or sparked the post, and the chips must reflect that.
   */
  protected onCloseVideo(): void {
    this.openVideo.set(undefined);
    this.reload();
  }

  private messageFor(error: unknown): string {
    const candidate = error as { error?: { message?: string }; message?: string };
    return (
      candidate?.error?.message ??
      candidate?.message ??
      'We could not load the creator video library.'
    );
  }
}
